import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Collapse,
  Paper,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  SelectAll as SelectAllIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { useOutlookPlugin } from "../../../hooks/useOutlookPlugin";
import type { ShipmentAction, GetShipmentActionsResponse } from "@services/demo/msoffice/types";
import { ActionCard } from "./ActionCard";

interface ShipmentActionsComponentProps {
  shipmentId: string;
  fetchActions: (shipmentId: string) => Promise<GetShipmentActionsResponse>;
}

export const ShipmentActionsComponent: React.FC<ShipmentActionsComponentProps> = ({
  shipmentId,
  fetchActions,
}) => {
  const { actions } = useOutlookPlugin();
  const [shipmentActions, setShipmentActions] = useState<ShipmentAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [checkedActions, setCheckedActions] = useState<Set<string>>(new Set());

  const fetchActionsRef = useRef(fetchActions);
  fetchActionsRef.current = fetchActions;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetchActionsRef.current(shipmentId);
        setShipmentActions(response.actions);
      } catch (err) {
        console.error("Error fetching shipment actions:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shipmentId]);

  const handleAction = (action: ShipmentAction) => {
    if (action.type === "draft_email" && action.draftConfig) {
      actions.draftEmail(
        action.draftConfig.to,
        action.draftConfig.subject,
        action.draftConfig.body,
        action.draftConfig.attachments
      );
    } else if (action.type === "write_value") {
      console.log("[Action] Write value:", action.id, action.label);
    }
  };

  const handleCheckboxChange = (id: string) => {
    setCheckedActions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (checkedActions.size === shipmentActions.length) {
      setCheckedActions(new Set());
    } else {
      setCheckedActions(new Set(shipmentActions.map((a) => a.id)));
    }
  };

  const handleExecuteAll = () => {
    const selected = shipmentActions.filter((a) => checkedActions.has(a.id));
    for (const action of selected) {
      handleAction(action);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (shipmentActions.length === 0) {
    return null;
  }

  const allSelected = checkedActions.size === shipmentActions.length && shipmentActions.length > 0;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        position: "relative",
      }}
    >
      {/* Accordion header */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.75,
          cursor: "pointer",
          bgcolor: "grey.50",
          "&:hover": { bgcolor: "grey.100" },
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          Actions
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: "text.secondary",
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: checkedActions.size > 0 ? 6 : 1 }}>
          {shipmentActions.map((action) => (
            <ActionCard
              key={action.id}
              id={action.id}
              title={action.label}
              type={action.type}
              buttonLabel={action.buttonLabel}
              draftConfig={action.draftConfig}
              checked={checkedActions.has(action.id)}
              onCheckedChange={handleCheckboxChange}
              onExecute={() => handleAction(action)}
            />
          ))}
        </Box>

        {/* Bottom bar when actions are checked */}
        {checkedActions.size > 0 && (
          <Paper
            elevation={4}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              zIndex: 999,
              bgcolor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button
              size="small"
              startIcon={<SelectAllIcon />}
              onClick={handleSelectAll}
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
            >
              {allSelected ? "Deselect All" : "Select All"}
            </Button>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {checkedActions.size} selected
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={handleExecuteAll}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
              }}
            >
              Execute All
            </Button>
          </Paper>
        )}
      </Collapse>
    </Box>
  );
};
